import React, { useState, useEffect, useRef } from 'react';
import './JavaInterviewPage.css';
import { sections } from './javaInterviewData';
import { useInterview } from './useInterviewContext';

/* ─── FULL INTEGRATED ARCHITECTURE DIAGRAM ─── */
function IntegratedArchDiagram() {
    return (
        <div className="ji-arch-wrap">
            <div className="ji-arch-title">🏗️ Full System Architecture — How All Technologies Connect</div>
            <div className="ji-arch-subtitle">A real-time e-commerce platform using the complete Java tech stack</div>
            <svg viewBox="0 0 900 520" width="100%" style={{ maxHeight: 520 }}>
                <defs>
                    <marker id="arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path d="M0,0 L8,3 L0,6 Z" fill="#475569" />
                    </marker>
                    <marker id="arr-yellow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path d="M0,0 L8,3 L0,6 Z" fill="#f59e0b" />
                    </marker>
                    <marker id="arr-green" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path d="M0,0 L8,3 L0,6 Z" fill="#22c55e" />
                    </marker>
                    <marker id="arr-purple" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path d="M0,0 L8,3 L0,6 Z" fill="#8b5cf6" />
                    </marker>
                </defs>

                {/* ── LAYER LABELS ── */}
                {[
                    { x: 10, y: 30, label: '① CLIENT', color: '#475569' },
                    { x: 10, y: 130, label: '② GATEWAY', color: '#475569' },
                    { x: 10, y: 230, label: '③ SERVICES', color: '#475569' },
                    { x: 10, y: 350, label: '④ MESSAGING', color: '#475569' },
                    { x: 10, y: 430, label: '⑤ DATA', color: '#475569' },
                ].map((l, i) => (
                    <text key={i} x={l.x} y={l.y} fill={l.color} fontSize={10} fontWeight={700} letterSpacing={1}>{l.label}</text>
                ))}

                {/* ── ROW 1: CLIENTS ── */}
                {['Web Browser', 'Mobile App', '3rd Party API'].map((label, i) => (
                    <g key={i}>
                        <rect x={120 + i * 220} y={10} width={150} height={40} rx={8} fill="#1e293b" stroke="#475569" strokeWidth={1.5} />
                        <text x={195 + i * 220} y={35} textAnchor="middle" fill="#94a3b8" fontSize={12} fontWeight={600}>{label}</text>
                    </g>
                ))}

                {/* ── ROW 2: API GATEWAY / VERT.X ── */}
                <rect x={120} y={110} width={700} height={50} rx={10} fill="rgba(99,102,241,0.15)" stroke="#6366f1" strokeWidth={2} />
                <text x={470} y={130} textAnchor="middle" fill="#a5b4fc" fontSize={13} fontWeight={700}>⚡ API Gateway (Vert.x Event Loop)</text>
                <text x={470} y={148} textAnchor="middle" fill="#64748b" fontSize={11}>Non-blocking · Load Balancing · Auth · Rate Limiting</text>

                {/* ── ARROWS: clients → gateway ── */}
                {[195, 415, 635].map((x, i) => (
                    <line key={i} x1={x} y1={50} x2={x} y2={110} stroke="#475569" strokeWidth={1.5} markerEnd="url(#arr)" />
                ))}

                {/* ── ROW 3: MICROSERVICES ── */}
                {[
                    { x: 120, label: '🍃 Order Service', sub: 'Spring Boot', color: '#22c55e' },
                    { x: 310, label: '💳 Payment Svc', sub: 'Spring Boot', color: '#22c55e' },
                    { x: 500, label: '📦 Inventory Svc', sub: 'Spring Boot', color: '#22c55e' },
                    { x: 690, label: '👤 User Service', sub: 'Spring Boot', color: '#22c55e' },
                ].map((s, i) => (
                    <g key={i}>
                        <rect x={s.x} y={210} width={170} height={60} rx={10} fill="rgba(34,197,94,0.1)" stroke={s.color} strokeWidth={1.5} />
                        <text x={s.x + 85} y={233} textAnchor="middle" fill="#86efac" fontSize={12} fontWeight={700}>{s.label}</text>
                        <text x={s.x + 85} y={250} textAnchor="middle" fill="#64748b" fontSize={11}>{s.sub}</text>
                        <line x1={s.x + 85} y1={160} x2={s.x + 85} y2={210} stroke="#22c55e" strokeWidth={1.5} markerEnd="url(#arr-green)" />
                    </g>
                ))}

                {/* ── IGNITE: In-memory cache layer ── */}
                <rect x={120} y={290} width={700} height={40} rx={8} fill="rgba(239,68,68,0.1)" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="6 3" />
                <text x={470} y={306} textAnchor="middle" fill="#fca5a5" fontSize={12} fontWeight={700}>🔥 Apache Ignite — In-Memory Data Grid</text>
                <text x={470} y={322} textAnchor="middle" fill="#64748b" fontSize={11}>Hot-path cache · Sub-millisecond reads · Distributed SQL · Sits between services and databases</text>
                {[205, 395, 585, 775].map((x, i) => (
                    <line key={i} x1={x} y1={270} x2={x} y2={290} stroke="#ef4444" strokeWidth={1} strokeDasharray="3 2" markerEnd="url(#arr)" />
                ))}

                {/* ── ROW 4: KAFKA ── */}
                <rect x={120} y={348} width={700} height={50} rx={10} fill="rgba(245,158,11,0.12)" stroke="#f59e0b" strokeWidth={2} />
                <text x={470} y={368} textAnchor="middle" fill="#fcd34d" fontSize={13} fontWeight={700}>📨 Apache Kafka — Event Bus</text>
                <text x={470} y={386} textAnchor="middle" fill="#64748b" fontSize={11}>orders.placed · payments.completed · inventory.updated · users.registered</text>

                {/* ── ROW 5: DATA LAYER ── */}
                {[
                    { x: 60, w: 150, label: '🐘 PostgreSQL', sub: 'Master data\nUsers / Products', color: '#0ea5e9', iconColor: '#7dd3fc' },
                    { x: 230, w: 150, label: '💾 Cassandra', sub: 'High-volume ops\nOrders history', color: '#a855f7', iconColor: '#d8b4fe' },
                    { x: 400, w: 150, label: '🔥 Ignite', sub: 'Real-time state\nLive inventory', color: '#ef4444', iconColor: '#fca5a5' },
                    { x: 570, w: 165, label: '✨ Spark ETL', sub: 'Batch transform\nKafka → Warehouse', color: '#8b5cf6', iconColor: '#c4b5fd' },
                    { x: 745, w: 155, label: '🏗️ Yellowbrick', sub: 'Analytics / BI\nReports / ML', color: '#f59e0b', iconColor: '#fcd34d' },
                ].map((d, i) => (
                    <g key={i}>
                        <rect x={d.x} y={420} width={d.w} height={80} rx={10} fill={`rgba(${d.color.startsWith('#0ea5e9') ? '14,165,233' : d.color.startsWith('#a855f7') ? '168,85,247' : d.color.startsWith('#ef4444') ? '239,68,68' : d.color.startsWith('#8b5cf6') ? '139,92,246' : '245,158,11'},0.12)`} stroke={d.color} strokeWidth={1.5} />
                        <text x={d.x + d.w / 2} y={442} textAnchor="middle" fill={d.iconColor} fontSize={12} fontWeight={700}>{d.label}</text>
                        {d.sub.split('\n').map((t, j) => (
                            <text key={j} x={d.x + d.w / 2} y={458 + j * 15} textAnchor="middle" fill="#64748b" fontSize={10}>{t}</text>
                        ))}
                    </g>
                ))}

                {/* ── ARROWS: Kafka → Data stores ── */}
                {[135, 305, 475, 652, 822].map((x, i) => (
                    <line key={i} x1={x} y1={398} x2={x} y2={420} stroke="#f59e0b" strokeWidth={1.5} markerEnd="url(#arr-yellow)" />
                ))}

                {/* ── SPARK → YELLOWBRICK ── */}
                <line x1={735} y1={460} x2={745} y2={460} stroke="#8b5cf6" strokeWidth={1.5} markerEnd="url(#arr-purple)" />

                {/* ── LEGEND ── */}
                <g transform="translate(60, 510)">
                    {[
                        { color: '#6366f1', label: 'Vert.x' },
                        { color: '#22c55e', label: 'Spring Boot' },
                        { color: '#f59e0b', label: 'Kafka events' },
                        { color: '#ef4444', label: 'Ignite cache' },
                        { color: '#8b5cf6', label: 'Spark/Yellowbrick' },
                    ].map((l, i) => (
                        <g key={i} transform={`translate(${i * 160},0)`}>
                            <rect y={-8} width={10} height={10} rx={2} fill={l.color} fillOpacity={0.6} />
                            <text x={14} y={1} fill="#64748b" fontSize={10}>{l.label}</text>
                        </g>
                    ))}
                </g>
            </svg>

            {/* Flow explanation */}
            <div className="ji-arch-flow">
                {[
                    { step: '1', color: '#6366f1', text: 'User places order → Vert.x API Gateway validates auth and routes to Order Service (Spring Boot)' },
                    { step: '2', color: '#22c55e', text: 'Order Service writes to PostgreSQL (ACID transaction) and checks Ignite cache for live inventory' },
                    { step: '3', color: '#f59e0b', text: 'Order Service publishes orders.placed event to Kafka. Payment, Inventory, and Notification services consume independently' },
                    { step: '4', color: '#ef4444', text: 'Ignite stores real-time order state for sub-millisecond lookups. Acts as L2 cache between services and DB' },
                    { step: '5', color: '#8b5cf6', text: 'Spark reads from Kafka, transforms data, loads into Cassandra (operational) and Yellowbrick (analytics/BI)' },
                ].map((f, i) => (
                    <div key={i} className="ji-arch-step">
                        <span className="ji-arch-step-num" style={{ background: f.color + '33', color: f.color, border: `1px solid ${f.color}66` }}>{f.step}</span>
                        <span className="ji-arch-step-text">{f.text}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ─── AMEX MERCHANT PAYMENT ARCHITECTURE ─── */

function CreditCardPaymentSection() {
    const phases = [
        { num: '1', color: '#6366f1', title: 'PHASE 1 — Authorization (real-time, <2 seconds, synchronous)', text: 'Cardholder swipes card at merchant POS → Vert.x API Gateway receives request (non-blocking, TLS) → Spring Boot Auth Service checks Apache Ignite cache for card status and credit limit (<1ms) → Spark Streaming fraud ML model scores the transaction (budget: 100ms) → PostgreSQL validates card account and merchant → APPROVED or DECLINED returned to POS' },
        { num: '2', color: '#f59e0b', title: 'PHASE 2 — Event Streaming (async, decoupled via Kafka)', text: 'Auth Service publishes to Kafka: transaction.authorized or transaction.declined → Cassandra consumer writes immutable transaction record (write-optimized, partition by merchant+date) → Notification Service sends cardholder receipt (email/SMS) → Ignite cache updates available spending balance in memory → Audit consumer logs to compliance store' },
        { num: '3', color: '#22c55e', title: 'PHASE 3 — End-of-Day Settlement (batch, Spring Boot + Cassandra)', text: 'Settlement Aggregator (Spring Boot Batch job, runs at midnight) reads all day\'s approved transactions from Cassandra → Groups by merchant → Subtracts interchange fees (card company retains 1.5-3%) → Initiates ACH wire transfer to merchant bank account → Publishes settlement.completed events to Kafka for audit trail → T+1 or T+2 payment depending on merchant contract' },
        { num: '4', color: '#a855f7', title: 'PHASE 4 — Analytics Pipeline (continuous, Spark + Yellowbrick)', text: 'Spark Streaming continuously reads from Kafka → Enriches transactions with merchant category, location, cardholder segment → Loads columnar data into Yellowbrick warehouse → BI tools query Yellowbrick for: fraud trend reports, merchant spend analytics, monthly cardholder statements, real-time risk dashboards, ML feature store for next-gen fraud models' },
    ];

    const techRoles = [
        { icon: '⚡', name: 'Vert.x', color: '#6366f1', role: 'API Gateway', detail: 'Handles 10K+ TPS non-blocking. TLS termination, auth token validation, rate limiting per card network rules (<50ms overhead)' },
        { icon: '🍃', name: 'Spring Boot', color: '#22c55e', role: 'Auth + Settlement Services', detail: 'Authorization microservice — stateless, horizontally scalable. Settlement batch processor using Spring Batch for nightly ACH runs' },
        { icon: '🔥', name: 'Ignite', color: '#ef4444', role: 'Card Member Hot Cache', detail: 'Caches: credit limit, account status, spending balance per card token. Sub-millisecond reads — critical to meet <2s auth SLA without hitting PostgreSQL every time' },
        { icon: '✨', name: 'Spark', color: '#8b5cf6', role: 'Fraud ML + ETL', detail: 'Streaming: real-time fraud model scoring (gradient boosted trees, 150 features). Batch: nightly settlement reconciliation and ETL from Kafka into Yellowbrick' },
        { icon: '📨', name: 'Kafka', color: '#f59e0b', role: 'Event Bus (decoupled)', detail: 'Topics: transaction.authorized, transaction.declined, settlement.initiated, merchant.paid. Each downstream service (Cassandra writer, notifier, analytics) consumes independently at its own pace' },
        { icon: '🐘', name: 'PostgreSQL', color: '#0ea5e9', role: 'Master / Reference Data', detail: 'Card member accounts, merchant registry, interchange rate tables, compliance records. Strong ACID — correctness is non-negotiable for financial data' },
        { icon: '💾', name: 'Cassandra', color: '#a855f7', role: 'Transaction Log Store', detail: 'Immutable transaction records written once, never updated. Handles 100M+ writes/day. Partition key: (merchant_id, date) for efficient settlement reads. Replication factor 3 for zero data loss' },
        { icon: '🏗️', name: 'Yellowbrick', color: '#f59e0b', role: 'Analytics Warehouse (OLAP)', detail: 'Columnar storage for complex analytical queries. Powers: fraud dashboards, merchant analytics, cardholder statements, ML feature pipelines. Handles years of transaction history efficiently' },
    ];

    return (
        <div className="ji-arch-wrap">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
                <span style={{ fontSize: 32 }}>💳</span>
                <div>
                    <div className="ji-arch-title">Credit Card Merchant Payment System Architecture</div>
                    <div className="ji-arch-subtitle">End-to-end: Cardholder swipes card → transaction authorized → merchant receives payment next day</div>
                </div>
            </div>

            {/* ── MAIN SVG DIAGRAM ── */}
            <svg viewBox="0 0 950 600" width="100%" style={{ maxHeight: 600, display: 'block', margin: '24px 0 0' }}>
                <defs>
                    {[['am1', '#6366f1'], ['am2', '#22c55e'], ['am3', '#f59e0b'], ['am4', '#a855f7'], ['am5', '#0ea5e9'], ['am6', '#ef4444'], ['am7', '#8b5cf6']].map(([id, fill]) => (
                        <marker key={id} id={id} markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
                            <path d="M0,0L7,3L0,6Z" fill={fill} />
                        </marker>
                    ))}
                </defs>

                {/* Phase band backgrounds */}
                <rect x={0} y={0} width={950} height={195} fill="rgba(99,102,241,0.04)" rx={0} />
                <rect x={0} y={200} width={950} height={135} fill="rgba(245,158,11,0.04)" rx={0} />
                <rect x={0} y={340} width={950} height={115} fill="rgba(34,197,94,0.04)" rx={0} />
                <rect x={0} y={460} width={950} height={130} fill="rgba(168,85,247,0.04)" rx={0} />

                {/* Phase labels */}
                {[
                    { y: 8, t: '① AUTHORIZATION  (real-time < 2s, synchronous)', c: '#818cf8' },
                    { y: 205, t: '② KAFKA EVENT BUS  (async, decoupled)', c: '#fbbf24' },
                    { y: 345, t: '③ END-OF-DAY SETTLEMENT  (batch, midnight)', c: '#86efac' },
                    { y: 465, t: '④ ANALYTICS PIPELINE  (continuous)', c: '#c4b5fd' },
                ].map((p, i) => <text key={i} x={8} y={p.y + 12} fill={p.c} fontSize={10} fontWeight={700}>{p.t}</text>)}

                {/* ══ PHASE 1: AUTHORIZATION ══ */}
                {/* Cardholder */}
                <rect x={8} y={28} width={85} height={55} rx={8} fill="rgba(99,102,241,.12)" stroke="#6366f1" strokeWidth={1.5} />
                <text x={50} y={51} textAnchor="middle" fill="#a5b4fc" fontSize={20}>👤</text>
                <text x={50} y={68} textAnchor="middle" fill="#e2e8f0" fontSize={10} fontWeight={700}>Cardholder</text>

                <line x1={93} y1={55} x2={115} y2={55} stroke="#6366f1" strokeWidth={1.5} markerEnd="url(#am1)" />

                {/* Merchant POS */}
                <rect x={115} y={28} width={90} height={55} rx={8} fill="rgba(71,85,105,.18)" stroke="#475569" strokeWidth={1.5} />
                <text x={160} y={50} textAnchor="middle" fill="#cbd5e1" fontSize={16}>🖥️</text>
                <text x={160} y={66} textAnchor="middle" fill="#e2e8f0" fontSize={10} fontWeight={700}>Merchant POS</text>
                <text x={160} y={78} textAnchor="middle" fill="#64748b" fontSize={9}>Terminal</text>

                <line x1={205} y1={55} x2={228} y2={55} stroke="#6366f1" strokeWidth={1.5} markerEnd="url(#am1)" />

                {/* Vert.x Gateway */}
                <rect x={228} y={20} width={135} height={70} rx={10} fill="rgba(99,102,241,.18)" stroke="#6366f1" strokeWidth={2} />
                <text x={295} y={44} textAnchor="middle" fill="#a5b4fc" fontSize={12} fontWeight={800}>⚡ Vert.x Gateway</text>
                <text x={295} y={59} textAnchor="middle" fill="#94a3b8" fontSize={10}>Non-blocking · TLS</text>
                <text x={295} y={72} textAnchor="middle" fill="#94a3b8" fontSize={10}>10K+ TPS · Rate Limit</text>

                <line x1={363} y1={55} x2={390} y2={55} stroke="#6366f1" strokeWidth={1.5} markerEnd="url(#am1)" />

                {/* Auth Service */}
                <rect x={390} y={20} width={150} height={70} rx={10} fill="rgba(34,197,94,.15)" stroke="#22c55e" strokeWidth={2} />
                <text x={465} y={44} textAnchor="middle" fill="#86efac" fontSize={12} fontWeight={800}>🍃 Auth Service</text>
                <text x={465} y={59} textAnchor="middle" fill="#94a3b8" fontSize={10}>Spring Boot · Stateless</text>
                <text x={465} y={72} textAnchor="middle" fill="#94a3b8" fontSize={10}>Horizontally scaled</text>

                {/* Auth → Ignite (down, dashed) */}
                <line x1={465} y1={90} x2={465} y2={115} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 2" markerEnd="url(#am6)" />

                {/* Auth → Spark Fraud (right) */}
                <line x1={540} y1={55} x2={570} y2={55} stroke="#8b5cf6" strokeWidth={1.5} markerEnd="url(#am7)" />

                {/* Auth → PostgreSQL (right, thin) */}
                <line x1={540} y1={65} x2={730} y2={65} stroke="#0ea5e9" strokeWidth={1.2} strokeDasharray="3 2" />
                <line x1={730} y1={65} x2={730} y2={115} stroke="#0ea5e9" strokeWidth={1.2} markerEnd="url(#am5)" />

                {/* Ignite Cache */}
                <rect x={390} y={115} width={150} height={65} rx={10} fill="rgba(239,68,68,.14)" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="6 3" />
                <text x={465} y={138} textAnchor="middle" fill="#fca5a5" fontSize={12} fontWeight={700}>🔥 Ignite Cache</text>
                <text x={465} y={153} textAnchor="middle" fill="#94a3b8" fontSize={10}>Credit limit · Acct status</text>
                <text x={465} y={166} textAnchor="middle" fill="#f59e0b" fontSize={10} fontWeight={600}>&lt; 1ms lookup (hot path)</text>

                {/* Spark Fraud */}
                <rect x={570} y={20} width={150} height={70} rx={10} fill="rgba(139,92,246,.15)" stroke="#8b5cf6" strokeWidth={1.5} />
                <text x={645} y={44} textAnchor="middle" fill="#c4b5fd" fontSize={12} fontWeight={700}>✨ Fraud ML</text>
                <text x={645} y={59} textAnchor="middle" fill="#94a3b8" fontSize={10}>Spark Streaming + XGBoost</text>
                <text x={645} y={72} textAnchor="middle" fill="#f59e0b" fontSize={10} fontWeight={600}>Score budget: 100ms</text>

                {/* PostgreSQL */}
                <rect x={730} y={115} width={140} height={65} rx={10} fill="rgba(14,165,233,.12)" stroke="#0ea5e9" strokeWidth={1.5} />
                <text x={800} y={138} textAnchor="middle" fill="#7dd3fc" fontSize={12} fontWeight={700}>🐘 PostgreSQL</text>
                <text x={800} y={153} textAnchor="middle" fill="#94a3b8" fontSize={10}>Card accounts · Merchant data</text>
                <text x={800} y={166} textAnchor="middle" fill="#94a3b8" fontSize={10}>Interchange rates (ACID)</text>

                {/* APPROVED response */}
                <text x={312} y={168} fill="#22c55e" fontSize={11} fontWeight={700}>✅ APPROVED response</text>
                <line x1={390} y1={162} x2={330} y2={162} stroke="#22c55e" strokeWidth={1.5} strokeDasharray="3 2" markerEnd="url(#am2)" />
                <text x={312} y={183} fill="#ef4444" fontSize={11} fontWeight={700}>❌ DECLINED response</text>

                {/* ══ PHASE 2: KAFKA ══ */}
                <rect x={8} y={205} width={930} height={55} rx={10} fill="rgba(245,158,11,.12)" stroke="#f59e0b" strokeWidth={2} />
                <text x={475} y={227} textAnchor="middle" fill="#fcd34d" fontSize={13} fontWeight={800}>📨 Apache Kafka — Event Bus</text>
                <text x={475} y={245} textAnchor="middle" fill="#94a3b8" fontSize={11}>transaction.authorized  ·  transaction.declined  ·  settlement.initiated  ·  merchant.paid</text>

                {/* Auth publishes to Kafka */}
                <path d="M 465 90 Q 420 195 350 205" fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 2" markerEnd="url(#am3)" />

                {/* ══ KAFKA CONSUMERS ══ */}
                {[
                    { x: 20, ico: '💾', top: 'Cassandra', bot: 'Transaction Log', color: '#a855f7' },
                    { x: 210, ico: '📱', top: 'Notification Svc', bot: 'Email / SMS receipt', color: '#06b6d4' },
                    { x: 400, ico: '🔥', top: 'Ignite Update', bot: 'Balance cache refresh', color: '#ef4444' },
                    { x: 590, ico: '🧮', top: 'Settlement Queue', bot: 'Merchant payment batch', color: '#22c55e' },
                    { x: 780, ico: '🔎', top: 'Audit / Compliance', bot: 'Immutable event log', color: '#64748b' },
                ].map((c, i) => (
                    <g key={i}>
                        <line x1={c.x + 90} y1={260} x2={c.x + 90} y2={290} stroke={c.color} strokeWidth={1.5} markerEnd="url(#am1)" />
                        <rect x={c.x} y={290} width={180} height={55} rx={8} fill="rgba(15,23,42,.6)" stroke={c.color} strokeWidth={1.5} />
                        <text x={c.x + 90} y={313} textAnchor="middle" fill="#e2e8f0" fontSize={15}>{c.ico}</text>
                        <text x={c.x + 90} y={328} textAnchor="middle" fill="#e2e8f0" fontSize={11} fontWeight={700}>{c.top}</text>
                        <text x={c.x + 90} y={340} textAnchor="middle" fill="#64748b" fontSize={10}>{c.bot}</text>
                    </g>
                ))}

                {/* ══ PHASE 3: SETTLEMENT ══ */}
                {/* Cassandra → Settlement Engine */}
                <line x1={110} y1={345} x2={375} y2={380} stroke="#a855f7" strokeWidth={1.2} strokeDasharray="4 2" markerEnd="url(#am4)" />
                <text x={230} y={368} fill="#a855f7" fontSize={10} textAnchor="middle">reads day&apos;s transactions</text>

                {/* Settlement Engine */}
                <rect x={375} y={380} width={200} height={65} rx={10} fill="rgba(34,197,94,.15)" stroke="#22c55e" strokeWidth={2} />
                <text x={475} y={404} textAnchor="middle" fill="#86efac" fontSize={13} fontWeight={800}>🧩 Settlement Engine</text>
                <text x={475} y={420} textAnchor="middle" fill="#94a3b8" fontSize={10}>Spring Boot Batch (midnight)</text>
                <text x={475} y={433} textAnchor="middle" fill="#94a3b8" fontSize={10}>Gross amount − interchange fees</text>

                <line x1={575} y1={412} x2={610} y2={412} stroke="#22c55e" strokeWidth={1.5} markerEnd="url(#am2)" />

                {/* Merchant Bank */}
                <rect x={610} y={380} width={180} height={65} rx={10} fill="rgba(34,197,94,.2)" stroke="#22c55e" strokeWidth={2} />
                <text x={700} y={404} textAnchor="middle" fill="#86efac" fontSize={13} fontWeight={800}>🏦 Merchant Bank</text>
                <text x={700} y={420} textAnchor="middle" fill="#94a3b8" fontSize={10}>ACH wire transfer</text>
                <text x={700} y={433} textAnchor="middle" fill="#fbbf24" fontSize={10} fontWeight={600}>T+1 or T+2 settlement</text>

                {/* Settlement publishes back to Kafka */}
                <path d="M 475 380 Q 475 260 600 250" fill="none" stroke="#f59e0b" strokeWidth={1} strokeDasharray="3 2" markerEnd="url(#am3)" />
                <text x={532} y={310} fill="#f59e0b" fontSize={9} textAnchor="middle">settlement.completed</text>

                {/* ══ PHASE 4: ANALYTICS ══ */}
                {/* Kafka → Spark ETL */}
                <line x1={90} y1={345} x2={50} y2={465} stroke="#f59e0b" strokeWidth={1.2} strokeDasharray="4 2" markerEnd="url(#am7)" />

                {/* Spark ETL */}
                <rect x={8} y={465} width={170} height={80} rx={10} fill="rgba(139,92,246,.15)" stroke="#8b5cf6" strokeWidth={1.5} />
                <text x={93} y={490} textAnchor="middle" fill="#c4b5fd" fontSize={12} fontWeight={700}>✨ Spark ETL</text>
                <text x={93} y={506} textAnchor="middle" fill="#94a3b8" fontSize={10}>Reads Kafka stream</text>
                <text x={93} y={519} textAnchor="middle" fill="#94a3b8" fontSize={10}>Enriches + partitions data</text>
                <text x={93} y={532} textAnchor="middle" fill="#94a3b8" fontSize={10}>Loads into Yellowbrick</text>

                <line x1={178} y1={505} x2={208} y2={505} stroke="#8b5cf6" strokeWidth={1.5} markerEnd="url(#am7)" />

                {/* Yellowbrick */}
                <rect x={208} y={465} width={190} height={80} rx={10} fill="rgba(245,158,11,.15)" stroke="#f59e0b" strokeWidth={2} />
                <text x={303} y={490} textAnchor="middle" fill="#fcd34d" fontSize={12} fontWeight={700}>🏗️ Yellowbrick</text>
                <text x={303} y={506} textAnchor="middle" fill="#94a3b8" fontSize={10}>Columnar warehouse</text>
                <text x={303} y={519} textAnchor="middle" fill="#94a3b8" fontSize={10}>Fraud trends · Merchant BI</text>
                <text x={303} y={532} textAnchor="middle" fill="#94a3b8" fontSize={10}>Cardholder statements</text>

                <line x1={398} y1={505} x2={428} y2={505} stroke="#f59e0b" strokeWidth={1.5} markerEnd="url(#am3)" />

                {/* BI Reports */}
                <rect x={428} y={465} width={510} height={80} rx={10} fill="rgba(15,23,42,.5)" stroke="#334155" strokeWidth={1} />
                <text x={683} y={488} textAnchor="middle" fill="#e2e8f0" fontSize={12} fontWeight={700}>BI Tools · Risk Dashboards · ML Feature Store</text>
                {[
                    { x: 440, y: 503, t: '📊 Fraud pattern alerts' },
                    { x: 440, y: 518, t: '📄 Monthly merchant statements' },
                    { x: 440, y: 533, t: '💳 Cardholder spend analytics' },
                    { x: 657, y: 503, t: '⚠️ Real-time risk dashboards' },
                    { x: 657, y: 518, t: '💹 Interchange revenue reports' },
                    { x: 657, y: 533, t: '🤖 ML training feature store' },
                ].map((b, i) => (
                    <text key={i} x={b.x} y={b.y} fill="#94a3b8" fontSize={10}>{b.t}</text>
                ))}

                {/* SLA footnote */}
                <text x={475} y={592} textAnchor="middle" fill="#334155" fontSize={11}>
                    SLA: Auth end-to-end &lt;2s  ·  Fraud score &lt;100ms  ·  Ignite lookup &lt;1ms  ·  Settlement T+1  ·  Analytics near real-time
                </text>
            </svg>

            {/* Phase flow cards */}
            <div className="ji-arch-flow" style={{ marginTop: 28 }}>
                {phases.map((p, i) => (
                    <div key={i} className="ji-arch-step">
                        <span className="ji-arch-step-num" style={{ background: p.color + '33', color: p.color, border: `1px solid ${p.color}66`, minWidth: 22, fontSize: 11 }}>{p.num}</span>
                        <div>
                            <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 13, marginBottom: 4 }}>{p.title}</div>
                            <div className="ji-arch-step-text">{p.text}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tech role grid */}
            <div style={{ marginTop: 28 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#a5b4fc', marginBottom: 16 }}>🛠️ Each Technology's Role in This System</div>
                <div className="ji-amex-tech-grid">
                    {techRoles.map((t, i) => (
                        <div key={i} className="ji-amex-tech-card" style={{ borderColor: t.color + '55' }}>
                            <div className="ji-amex-tech-head">
                                <span style={{ fontSize: 20 }}>{t.icon}</span>
                                <div>
                                    <div style={{ fontWeight: 700, color: t.color, fontSize: 13 }}>{t.name}</div>
                                    <div style={{ fontSize: 11, color: '#64748b' }}>{t.role}</div>
                                </div>
                            </div>
                            <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.65 }}>{t.detail}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Code snippet */}
            <div className="ji-code-block" style={{ marginTop: 24 }}>
                <div className="ji-code-label">💻 Authorization Flow — Vert.x + Spring Boot + Ignite + Kafka</div>
                <pre>{`// Step 1: Vert.x routes the POS request (non-blocking)
router.post("/api/v1/authorize").handler(ctx -> {
  AuthRequest req = ctx.body().asPojo(AuthRequest.class);
  vertx.eventBus().<String>request("auth.process", req, reply -> {
    ctx.response().end(reply.result().body());
  });
});

// Step 2: Auth Service — Ignite first, PostgreSQL fallback
@Service
public class AuthorizationService {
  private final IgniteCache<String, CardMemberState> cache; // sub-ms
  private final FraudScoringClient fraudClient;             // Spark ML
  private final CardMemberRepository pgRepo;                // PG fallback

  public AuthDecision authorize(AuthRequest req) {
    // Check Ignite cache first (avoids DB round-trip every authorization)
    CardMemberState state = cache.get(req.getCardToken());
    if (state == null) {
      state = pgRepo.findByToken(req.getCardToken()); // cache miss
      cache.put(req.getCardToken(), state);           // warm cache
    }

    if (req.getAmount() > state.getAvailableCredit())
      return AuthDecision.DECLINED_INSUFFICIENT_FUNDS;

    // Real-time fraud scoring — Spark Streaming model (must return <100ms)
    FraudScore score = fraudClient.score(req);
    if (score.getRisk() > FRAUD_THRESHOLD)
      return AuthDecision.DECLINED_FRAUD_SUSPECTED;

    // Approved: publish event to Kafka
    kafka.send("transaction.authorized", req.getCardToken(),
        new TransactionEvent(req, APPROVED, score));

    // Update balance in Ignite (no DB write needed — eventual consistency OK)
    state.debit(req.getAmount());
    cache.put(req.getCardToken(), state);
    return AuthDecision.APPROVED;
  }
}

// Step 3: Settlement Aggregator (Spring Batch, runs midnight)
@Bean
public Job settlementJob() {
  return jobBuilder.get("nightly-settlement")
    .start(readApprovedTxnsFromCassandra())   // partition by merchant + date
    .next(groupAndSumByMerchant())            // aggregate gross amounts
    .next(subtractInterchangeFees())          // Card company keeps ~2% interchange
    .next(initiateACHTransfers())             // wire to merchant bank accounts
    .next(publishSettlementEvents())          // Kafka: merchant.paid events
    .build();
}

# 🐍 Python Equivalent Strategy:
# - Vert.x Routing -> FastAPI async routes
# - Spring Service -> Python Class/Dependency Injection
# - Kafka Template -> confluent_kafka Producer
# - Spring Batch -> Celery or Prefect for workflow orchestration
# - Ignite Cache -> Redis or a distributed dictionary wrapper`}
                </pre>
            </div>
        </div>
    );
}

/* ─── PER-SECTION SVG DIAGRAMS ─── */

// Shared theme helpers
const T = { // text colors
    head: '#f8fafc',   // bright white for titles
    body: '#cbd5e1',   // slate-300 for sub-text
    dim: '#64748b',   // slate-500 for footnotes
    note: '#94a3b8',   // slate-400 for secondary
};
const BOX = (c) => ({ fill: c, fillOpacity: 0.15, stroke: c, strokeWidth: 1.8 });

const diagrams = {
    /* ── VERT.X ── */
    vertx: <svg viewBox="0 0 760 170" width="100%">
        <defs><marker id="vx-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0L8,3L0,6Z" fill="#6366f1" /></marker></defs>
        {[
            { x: 10, l: 'HTTP\nRequest', c: '#475569' },
            { x: 158, l: 'Event\nLoop', c: '#6366f1' },
            { x: 306, l: 'Verticle\n(Handler)', c: '#6366f1' },
            { x: 454, l: 'Event\nBus', c: '#8b5cf6' },
            { x: 602, l: 'Worker /\nDB Client', c: '#06b6d4' },
        ].map((n, i, a) => <g key={i}>
            <rect x={n.x} y={35} width={138} height={64} rx={10} fill={n.c} fillOpacity={.15} stroke={n.c} strokeWidth={1.8} />
            {n.l.split('\n').map((t, j) => <text key={j} x={n.x + 69} y={58 + j * 20} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={12} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
            {i < a.length - 1 && <line x1={n.x + 138} y1={67} x2={a[i + 1].x} y2={67} stroke={n.c} strokeWidth={1.8} markerEnd="url(#vx-a)" />}
        </g>)}
        <text x={380} y={130} textAnchor="middle" fill={T.dim} fontSize={11}>Never block the Event Loop • Use Worker Verticle for blocking I/O • Event Bus = internal messaging between Verticles</text>
        <text x={380} y={148} textAnchor="middle" fill={T.dim} fontSize={11}>Non-blocking + reactive = handles 10K+ concurrent connections on a single thread</text>
    </svg>,

    /* ── SPRING BOOT ── */
    springboot: <svg viewBox="0 0 760 170" width="100%">
        <defs><marker id="sb-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0L8,3L0,6Z" fill="#22c55e" /></marker></defs>
        {['Client\n(Browser)', '@RestController', '@Service\n(Business)', '@Repository\n(JPA)', 'PostgreSQL\nDatabase'].map((l, i, a) => <g key={i}>
            <rect x={20 + i * 148} y={35} width={138} height={64} rx={10} fill="#22c55e" fillOpacity={i === 0 || i === 4 ? .08 : .17} stroke="#22c55e" strokeWidth={i === 0 || i === 4 ? 1 : 2} />
            {l.split('\n').map((t, j) => <text key={j} x={89 + i * 148} y={58 + j * 20} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={11} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
            {i < a.length - 1 && <line x1={158 + i * 148} y1={67} x2={168 + i * 148} y2={67} stroke="#22c55e" strokeWidth={1.8} markerEnd="url(#sb-a)" />}
        </g>)}
        <text x={380} y={130} textAnchor="middle" fill={T.dim} fontSize={11}>HTTP flows: Client → Controller (routing) → Service (business logic) → Repository → DB → response back up the chain</text>
        <text x={380} y={148} textAnchor="middle" fill={T.dim} fontSize={11}>Auto-configuration + Dependency Injection via Spring IoC container</text>
    </svg>,

    /* ── KAFKA ── */
    kafka: <svg viewBox="0 0 800 230" width="100%">
        {/* Producer */}
        <rect x={10} y={80} width={120} height={55} rx={8} fill="#f59e0b" fillOpacity={.18} stroke="#f59e0b" strokeWidth={1.8} />
        <text x={70} y={103} textAnchor="middle" fill="#fcd34d" fontSize={13} fontWeight={700}>Producer</text>
        <text x={70} y={120} textAnchor="middle" fill={T.body} fontSize={10}>Order Svc</text>
        {/* Topic border */}
        <rect x={148} y={10} width={190} height={185} rx={10} fill="none" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="6 3" />
        <text x={243} y={208} textAnchor="middle" fill="#f59e0b" fontSize={11} fontWeight={700}>Topic: orders.placed</text>
        {/* Partitions */}
        {[0, 1, 2].map(i => <g key={i}>
            <rect x={158} y={20 + i * 58} width={170} height={44} rx={6} fill="#1e293b" stroke="#334155" strokeWidth={1.2} />
            <text x={243} y={39 + i * 58} textAnchor="middle" fill={T.head} fontSize={12} fontWeight={700}>Partition {i}</text>
            <text x={243} y={55 + i * 58} textAnchor="middle" fill={T.dim} fontSize={10}>offset: 0→1→2→... (immutable)</text>
        </g>)}
        {/* Arrow producer → partitions */}
        <line x1={130} y1={107} x2={158} y2={107} stroke="#f59e0b" strokeWidth={1.8} />
        {/* Consumers */}
        {[
            { y: 18, l: 'Inventory Svc\nGroup: inventory', c: '#06b6d4' },
            { y: 90, l: 'Email Svc\nGroup: email', c: '#22c55e' },
            { y: 162, l: 'Analytics Svc\nGroup: analytics', c: '#a855f7' },
        ].map((c, i) => <g key={i}>
            <rect x={420} y={c.y} width={185} height={52} rx={8} fill={c.c} fillOpacity={.14} stroke={c.c} strokeWidth={1.8} />
            {c.l.split('\n').map((t, j) => <text key={j} x={512} y={c.y + 22 + j * 18} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={12} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
            <line x1={338} y1={32 + i * 58} x2={420} y2={c.y + 26} stroke={c.c} strokeWidth={1.3} strokeDasharray="4 2" />
        </g>)}
        {/* Explanatory note */}
        <text x={615} y={190} textAnchor="middle" fill={T.dim} fontSize={11}>Each group reads independently</text>
        <text x={615} y={204} textAnchor="middle" fill={T.dim} fontSize={11}>at its own pace (own offset)</text>
    </svg>,

    /* ── IGNITE ── */
    ignite: <svg viewBox="0 0 760 200" width="100%">
        {[
            { x: 20, label: 'Node 1', parts: 'Partitions 0–33%\n(Primary)', back: 'Backup: partitions 67–100%' },
            { x: 285, label: 'Node 2', parts: 'Partitions 34–66%\n(Primary)', back: 'Backup: partitions 0–33%' },
            { x: 550, label: 'Node 3', parts: 'Partitions 67–100%\n(Primary)', back: 'Backup: partitions 34–66%' },
        ].map((n, i) => <g key={i}>
            <rect x={n.x} y={15} width={190} height={140} rx={12} fill="rgba(239,68,68,.1)" stroke="#ef4444" strokeWidth={1.8} />
            <text x={n.x + 95} y={40} textAnchor="middle" fill="#fca5a5" fontSize={13} fontWeight={700}>{n.label}</text>
            {n.parts.split('\n').map((t, j) => <text key={j} x={n.x + 95} y={62 + j * 18} textAnchor="middle" fill={T.head} fontSize={11}>{t}</text>)}
            <rect x={n.x + 10} y={100} width={170} height={32} rx={6} fill="rgba(239,68,68,.2)" />
            <text x={n.x + 95} y={121} textAnchor="middle" fill={T.note} fontSize={10}>{n.back}</text>
        </g>)}
        <text x={380} y={173} textAnchor="middle" fill={T.dim} fontSize={11}>Affinity routing: request for key X → goes directly to the node that owns key X → zero extra network hop</text>
        <text x={380} y={189} textAnchor="middle" fill={T.dim} fontSize={11}>In-memory storage = sub-millisecond reads • Persistence optional for durability</text>
    </svg>,

    /* ── SPARK ── */
    spark: <svg viewBox="0 0 760 180" width="100%">
        <defs><marker id="sp-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0L8,3L0,6Z" fill="#8b5cf6" /></marker></defs>
        {[
            { l: 'Data Source\n(S3/Kafka/HDFS)', c: '#64748b' },
            { l: 'Parse into\nDataFrame', c: '#8b5cf6' },
            { l: 'Transformations\n(LAZY — no exec)', c: '#a855f7' },
            { l: 'Action Called\n(.count / .write)', c: '#7c3aed' },
            { l: 'Catalyst builds\noptimal DAG', c: '#6d28d9' },
        ].map((n, i, a) => <g key={i}>
            <rect x={10 + i * 150} y={30} width={140} height={70} rx={10} fill={n.c} fillOpacity={.16} stroke={n.c} strokeWidth={1.8} />
            {n.l.split('\n').map((t, j) => <text key={j} x={80 + i * 150} y={57 + j * 22} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={11} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
            {i < a.length - 1 && <line x1={150 + i * 150} y1={65} x2={160 + i * 150} y2={65} stroke={n.c} strokeWidth={1.8} markerEnd="url(#sp-a)" />}
        </g>)}
        <text x={380} y={130} textAnchor="middle" fill={T.dim} fontSize={11}>Nothing executes until an Action is called — all transformations are lazy and form a DAG</text>
        <text x={380} y={148} textAnchor="middle" fill={T.dim} fontSize={11}>Wide transforms (groupBy / join) trigger data shuffles across nodes — expensive, minimize them</text>
    </svg>,

    /* ── POSTGRESQL ── */
    postgres: <svg viewBox="0 0 760 200" width="100%">
        <defs><marker id="pg-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0L8,3L0,6Z" fill="#0ea5e9" /></marker></defs>
        {/* Top row */}
        {[
            { x: 10, y: 30, w: 145, h: 58, l: 'App / ORM\n(Hibernate)', c: '#0ea5e9' },
            { x: 175, y: 30, w: 145, h: 58, l: 'Query Planner\n(EXPLAIN)', c: '#0ea5e9' },
            { x: 340, y: 30, w: 145, h: 58, l: 'Index Lookup\n(B-Tree / GIN)', c: '#22c55e' },
            { x: 505, y: 30, w: 235, h: 58, l: 'Shared Buffer Pool\n(30% of RAM, cache)', c: '#6366f1' },
        ].map((n, i) => <g key={i}>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} rx={8} fill={n.c} fillOpacity={.16} stroke={n.c} strokeWidth={1.8} />
            {n.l.split('\n').map((t, j) => <text key={j} x={n.x + n.w / 2} y={n.y + 22 + j * 18} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={11} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
        </g>)}
        {/* Connector top row */}
        {[[155, 59, 175, 59], [320, 59, 340, 59], [485, 59, 505, 59]].map(([x1, y1, x2, y2], i) =>
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0ea5e9" strokeWidth={1.6} markerEnd="url(#pg-a)" />)}
        {/* Bottom row */}
        {[
            { x: 175, y: 118, w: 145, h: 48, l: 'MVCC Rows\n(xmin / xmax)', c: '#f59e0b' },
            { x: 340, y: 118, w: 145, h: 48, l: 'WAL Log\n(Durability)', c: '#ef4444' },
            { x: 505, y: 118, w: 235, h: 48, l: 'Disk (Fallback\nif not cached)', c: '#64748b' },
        ].map((n, i) => <g key={i}>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} rx={8} fill={n.c} fillOpacity={.15} stroke={n.c} strokeWidth={1.8} />
            {n.l.split('\n').map((t, j) => <text key={j} x={n.x + n.w / 2} y={n.y + 18 + j * 18} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={11} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
        </g>)}
        <text x={380} y={183} textAnchor="middle" fill={T.dim} fontSize={11}>MVCC: readers see consistent snapshot without locks • Buffer cache hit = fast • WAL ensures crash recovery</text>
    </svg>,

    /* ── CASSANDRA ── */
    cassandra: <svg viewBox="0 0 760 270" width="100%">
        {/* Coordinator */}
        <circle cx={380} cy={70} r={58} fill="rgba(168,85,247,.12)" stroke="#a855f7" strokeWidth={2} />
        <text x={380} y={63} textAnchor="middle" fill="#d8b4fe" fontSize={14} fontWeight={700}>Coordinator</text>
        <text x={380} y={82} textAnchor="middle" fill={T.note} fontSize={11}>Node (any node)</text>
        {/* Replica nodes — moved up so there's room below */}
        {[{ cx: 110, cy: 185 }, { cx: 380, cy: 205 }, { cx: 650, cy: 185 }].map((n, i) => <g key={i}>
            <circle cx={n.cx} cy={n.cy} r={55} fill="rgba(99,102,241,.12)" stroke="#6366f1" strokeWidth={1.8} />
            <text x={n.cx} y={n.cy - 10} textAnchor="middle" fill="#a5b4fc" fontSize={13} fontWeight={700}>Node {i + 1}</text>
            <text x={n.cx} y={n.cy + 8} textAnchor="middle" fill={T.body} fontSize={10}>Partition replica</text>
            <text x={n.cx} y={n.cy + 24} textAnchor="middle" fill={T.dim} fontSize={10}>Primary + Backup</text>
            <line x1={380} y1={128} x2={n.cx} y2={n.cy - 55} stroke="#a855f7" strokeWidth={1.5} strokeDasharray="5 3" />
        </g>)}
        {/* QUORUM label — clearly below all circles */}
        <text x={380} y={255} textAnchor="middle" fill="#f59e0b" fontSize={12} fontWeight={700}>QUORUM write = 2 of 3 nodes must acknowledge before success</text>
    </svg>,

    /* ── YELLOWBRICK ── */
    yellowbrick: <svg viewBox="0 0 760 190" width="100%">
        <defs><marker id="yb-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0L8,3L0,6Z" fill="#f59e0b" /></marker></defs>
        {[
            { l: 'Kafka\nEvents', c: '#64748b' },
            { l: 'Spark ETL\n(Transform)', c: '#8b5cf6' },
            { l: 'Yellowbrick\nWarehouse', c: '#f59e0b' },
            { l: 'BI Tools\n(Tableau)', c: '#22c55e' },
        ].map((n, i, a) => <g key={i}>
            <rect x={20 + i * 185} y={30} width={175} height={72} rx={10} fill={n.c} fillOpacity={i === 2 ? .22 : .14} stroke={n.c} strokeWidth={i === 2 ? 2.2 : 1.8} />
            {n.l.split('\n').map((t, j) => <text key={j} x={107 + i * 185} y={58 + j * 22} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={13} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
            {i < a.length - 1 && <line x1={195 + i * 185} y1={66} x2={205 + i * 185} y2={66} stroke="#f59e0b" strokeWidth={1.8} markerEnd="url(#yb-a)" />}
        </g>)}
        <rect x={390} y={10} width={165} height={18} rx={4} fill="rgba(239,68,68,.15)" stroke="#ef4444" strokeWidth={1} />
        <text x={472} y={23} textAnchor="middle" fill="#fca5a5" fontSize={10}>NOT for CRUD transactions</text>
        <text x={380} y={130} textAnchor="middle" fill={T.head} fontSize={12} fontWeight={600}>Yellowbrick: columnar storage → reads ONLY needed columns → 10–100× faster for analytics queries</text>
        <text x={380} y={150} textAnchor="middle" fill={T.dim} fontSize={11}>Massively Parallel Processing (MPP) — each node processes a slice of the data simultaneously</text>
        <text x={380} y={168} textAnchor="middle" fill={T.dim} fontSize={11}>Ideal for: fraud trend reports, merchant spend analytics, monthly statements, ML feature store</text>
    </svg>,

    /* ── DISTRIBUTED SYSTEMS ── */
    distributed: <svg viewBox="0 0 760 230" width="100%">
        <defs><marker id="ds-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0L8,3L0,6Z" fill="#6366f1" /></marker></defs>
        {[
            { x: 10, y: 90, w: 120, h: 55, l: 'Client', c: '#06b6d4' },
            { x: 158, y: 90, w: 145, h: 55, l: 'API Gateway\n(Vert.x)', c: '#6366f1' },
            { x: 335, y: 20, w: 160, h: 55, l: 'Service A\n+ Circuit Breaker', c: '#22c55e' },
            { x: 335, y: 130, w: 160, h: 55, l: 'Service B\n(Saga Step)', c: '#a855f7' },
            { x: 525, y: 20, w: 160, h: 55, l: 'Kafka\nEvent Bus', c: '#f59e0b' },
            { x: 525, y: 130, w: 160, h: 55, l: 'Database\n(PG / Ignite)', c: '#ef4444' },
        ].map((n, i) => <g key={i}>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} rx={10} fill={n.c} fillOpacity={.14} stroke={n.c} strokeWidth={1.8} />
            {n.l.split('\n').map((t, j) => <text key={j} x={n.x + n.w / 2} y={n.y + 22 + j * 18} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={12} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
        </g>)}
        {/* Connectors */}
        <line x1={130} y1={117} x2={158} y2={117} stroke="#6366f1" strokeWidth={1.8} markerEnd="url(#ds-a)" />
        <line x1={303} y1={117} x2={335} y2={47} stroke="#22c55e" strokeWidth={1.5} strokeDasharray="4 2" />
        <line x1={303} y1={117} x2={335} y2={157} stroke="#a855f7" strokeWidth={1.5} strokeDasharray="4 2" />
        <line x1={495} y1={47} x2={525} y2={47} stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 2" />
        <line x1={495} y1={157} x2={525} y2={157} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 2" />
        <text x={380} y={205} textAnchor="middle" fill={T.dim} fontSize={11}>Circuit Breaker: stops cascading failures by failing fast when a downstream service is down</text>
        <text x={380} y={222} textAnchor="middle" fill={T.dim} fontSize={11}>Saga Pattern: chain of local transactions with compensating transactions on failure — no distributed locks</text>
    </svg>,

    /* ── AGILE ── */
    agile: <svg viewBox="0 0 760 185" width="100%">
        {['Backlog\nGrooming', 'Sprint\nPlanning', 'Build &\nTDD', 'Sprint\nReview', 'Retro &\nImprove'].map((l, i, a) => <g key={i}>
            <rect x={18 + i * 148} y={25} width={138} height={72} rx={10} fill="#10b981" fillOpacity={.13 + i * .025} stroke="#10b981" strokeWidth={1.8} />
            {l.split('\n').map((t, j) => <text key={j} x={87 + i * 148} y={52 + j * 22} textAnchor="middle" fill={j === 0 ? T.head : T.body} fontSize={12} fontWeight={j === 0 ? 700 : 400}>{t}</text>)}
            {i < a.length - 1 && <line x1={156 + i * 148} y1={61} x2={166 + i * 148} y2={61} stroke="#10b981" strokeWidth={1.8} />}
        </g>)}
        <text x={380} y={125} textAnchor="middle" fill="#10b981" fontSize={12} fontWeight={600}>← 2-week sprint cycle repeats continuously</text>
        <text x={380} y={148} textAnchor="middle" fill={T.dim} fontSize={11}>Each sprint = plan → build → test → demo → retrospect. Velocity improves over time through team learning.</text>
        <text x={380} y={166} textAnchor="middle" fill={T.dim} fontSize={11}>Backlog is priority-ordered by business value. Team pulls only what they can complete in the sprint.</text>
    </svg>,

    /* ── OOP ── */
    oop: <svg viewBox="0 0 760 340" width="100%">
        <defs><marker id="oop-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0L8,3L0,6Z" fill="#06b6d4" /></marker></defs>
        {/* 4 Pillars */}
        {[
            { x: 10, label: 'Encapsulation', sub: 'private fields\npublic methods', c: '#6366f1', desc: '🔒' },
            { x: 200, label: 'Inheritance', sub: 'extends parent\nis-a relationship', c: '#22c55e', desc: '🧬' },
            { x: 390, label: 'Polymorphism', sub: 'override methods\none interface', c: '#f59e0b', desc: '🎭' },
            { x: 580, label: 'Abstraction', sub: 'interface / abstract\nhide complexity', c: '#a855f7', desc: '🎯' },
        ].map((p, i) => <g key={i}>
            <rect x={p.x} y={10} width={180} height={90} rx={10} fill={p.c} fillOpacity={.14} stroke={p.c} strokeWidth={2} />
            <text x={p.x + 20} y={36} fill={p.c} fontSize={18}>{p.desc}</text>
            <text x={p.x + 90} y={36} textAnchor="middle" fill={T.head} fontSize={13} fontWeight={800}>{p.label}</text>
            {p.sub.split('\n').map((t, j) => <text key={j} x={p.x + 90} y={58 + j * 18} textAnchor="middle" fill={T.body} fontSize={11}>{t}</text>)}
        </g>)}
        {/* Abstract base class */}
        <rect x={290} y={135} width={180} height={60} rx={10} fill="rgba(6,182,212,.15)" stroke="#06b6d4" strokeWidth={2} />
        <text x={380} y={157} textAnchor="middle" fill="#22d3ee" fontSize={14} fontWeight={800}>abstract Payment</text>
        <text x={380} y={174} textAnchor="middle" fill={T.note} fontSize={10}>abstract process(amount)</text>
        <text x={380} y={188} textAnchor="middle" fill={T.note} fontSize={10}>execute() — template method</text>
        {/* Subclasses — moved well down */}
        {[
            { x: 40, label: 'CreditCard\nPayment', c: '#22c55e' },
            { x: 295, label: 'Crypto\nPayment', c: '#a855f7' },
            { x: 550, label: 'Bank\nTransfer', c: '#f59e0b' },
        ].map((s, i) => <g key={i}>
            <rect x={s.x} y={240} width={175} height={52} rx={8} fill={s.c} fillOpacity={.13} stroke={s.c} strokeWidth={1.8} />
            {s.label.split('\n').map((t, j) => <text key={j} x={s.x + 87} y={262 + j * 18} textAnchor="middle" fill={T.head} fontSize={12} fontWeight={700}>{t}</text>)}
            <line x1={s.x + 87} y1={240} x2={380} y2={195} stroke={s.c} strokeWidth={1.5} strokeDasharray="5 2" markerEnd="url(#oop-a)" />
        </g>)}
        {/* SOLID badges — clearly below subclasses */}
        {[
            { l: 'S', tip: 'Single Responsibility' },
            { l: 'O', tip: 'Open / Closed' },
            { l: 'L', tip: 'Liskov Substitution' },
            { l: 'I', tip: 'Interface Segregation' },
            { l: 'D', tip: 'Dependency Inversion' },
        ].map((b, i) => <g key={i}>
            <rect x={10 + i * 150} y={307} width={140} height={26} rx={7} fill="rgba(99,102,241,.18)" stroke="#6366f1" strokeWidth={1.2} />
            <text x={80 + i * 150} y={325} textAnchor="middle" fill="#a5b4fc" fontSize={12} fontWeight={800}>{b.l} — {b.tip}</text>
        </g>)}
    </svg>,
};

const diagramKeys = ['vertx', 'springboot', 'kafka', 'ignite', 'spark', 'postgres', 'cassandra', 'yellowbrick', 'distributed', 'agile', 'oop'];

function AccordionItem({ q, a, star }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`ji-accordion-item${open ? ' open' : ''}`}>
            <button className="ji-accordion-trigger" onClick={() => setOpen(o => !o)}>
                <span className="ji-accordion-q">{q}</span>
                <span className="ji-accordion-icon">+</span>
            </button>
            <div className="ji-accordion-body">
                <div className="ji-accordion-content">
                    <div className="ji-answer-label">✅ Model Answer</div>
                    {star && <div className="ji-star-badge">⭐ Use STAR format</div>}
                    <div className="ji-answer-text" dangerouslySetInnerHTML={{
                        __html: a.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>')
                    }} />
                </div>
            </div>
        </div>
    );
}

export default function JavaInterviewPage() {
    const { toggleCoach, setActiveTopic } = useInterview();
    const [activeId, setActiveId] = useState('architecture');
    const refs = useRef({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const obs = new IntersectionObserver(
            es => es.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }),
            { rootMargin: '-15% 0px -75% 0px' }
        );
        Object.values(refs.current).forEach(el => el && obs.observe(el));
        return () => obs.disconnect();
    }, []);

    const scrollTo = id => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const navItems = [
        { id: 'architecture', icon: '🏗️', label: 'Full Architecture' },
        { id: 'amex-payment', icon: '💳', label: 'Credit Card Payment System' },
        ...sections.map(s => ({ id: s.id, icon: s.icon, label: s.title })),
    ];


    return (
        <div className="ji-page">
            <div className="ji-topbar">
                <button className="ji-back-btn" onClick={() => { window.location.hash = 'swe'; }}>← Back to SWE</button>
                <div style={{ textAlign: 'center' }}>
                    <div className="ji-topbar-title">☕ Java Interview Deep-Dive</div>
                    <div className="ji-topbar-subtitle">Vert.x · Spring Boot · Kafka · Ignite · Spark · PostgreSQL · Cassandra · Yellowbrick</div>
                </div>
                <div style={{ width: 130 }} />
            </div>

            <div className="ji-body">
                <nav className="ji-sidebar">
                    <div className="ji-sidebar-title">Sections</div>
                    {navItems.map(n => (
                        <button key={n.id} className={`ji-nav-item${activeId === n.id ? ' active' : ''}`} onClick={() => scrollTo(n.id)}>
                            {n.icon} {n.label}
                        </button>
                    ))}
                </nav>

                <main className="ji-main">
                    {/* ── INTEGRATED ARCH DIAGRAM ── */}
                    <section id="architecture" className="ji-section" ref={el => (refs.current['architecture'] = el)}>
                        <IntegratedArchDiagram />
                    </section>

                    {/* ── AMEX PAYMENT ARCHITECTURE ── */}
                    <section id="amex-payment" className="ji-section" ref={el => (refs.current['amex-payment'] = el)}>
                        <CreditCardPaymentSection />
                    </section>

                    {/* ── TECH SECTIONS ── */}
                    {sections.map((s, idx) => (
                        <section key={s.id} id={s.id} className="ji-section" ref={el => (refs.current[s.id] = el)}>
                            <div className="ji-section-header">
                                <div className="ji-section-icon" style={{ background: s.bg }}>{s.icon}</div>
                                <div className="ji-section-meta">
                                    <h2>{s.icon} {s.title} <span className="ji-tagline">— {s.tagline}</span></h2>
                                </div>
                                <button
                                    className="ji-practice-btn"
                                    onClick={() => {
                                        setActiveTopic({ ...s, type: 'swe' });
                                        toggleCoach(true);
                                    }}
                                >
                                    🤖 Practice with AI
                                </button>
                            </div>
                            <div className="ji-section-divider" />

                            {/* Simple explanation */}
                            <div className="ji-simple-box">
                                <div className="ji-simple-label">📖 Simple Explanation</div>
                                <p>{s.simple}</p>
                            </div>

                            {/* Analogy */}
                            <div className="ji-analogy-box">
                                <div className="ji-analogy-label">💡 Analogy</div>
                                <p>{s.analogy}</p>
                            </div>

                            {/* Per-section diagram */}
                            {diagrams[diagramKeys[idx]] && (
                                <div className="ji-diagram-wrap">
                                    <div className="ji-diagram-label">📐 How It Works</div>
                                    {diagrams[diagramKeys[idx]]}
                                </div>
                            )}

                            {/* Key points */}
                            <div className="ji-keypoints">
                                <h4>🧠 Must-Know for Interview</h4>
                                <ul>{s.keyPoints.map((p, i) => <li key={i}>{p}</li>)}</ul>
                            </div>

                            {/* Code */}
                            {s.code && (
                                <div className="ji-code-block">
                                    <div className="ji-code-label">💻 Code Reference (with comments)</div>
                                    <pre>{s.code}</pre>
                                </div>
                            )}

                            {/* Q&A */}
                            <div className="ji-qa-title">❓ Interview Q&amp;A — Click to Expand Answers</div>
                            <div className="ji-accordion">
                                {s.qa.map((item, i) => <AccordionItem key={i} q={item.q} a={item.a} star={item.star} />)}
                            </div>
                        </section>
                    ))}
                </main>
            </div>
        </div>
    );
}
